import type { SmsStatus } from '@/lib/api/notifications.api'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { SlackLogo } from '@/components/ui/slack-logo'
import { Switch } from '@/components/ui/switch'
import { useAccountType } from '@/hooks/useAccountType'
import { useBrowserNotifications } from '@/hooks/useBrowserNotifications'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useSlackUserLink, useSlackWorkspace } from '@/hooks/useIntegrations'
import { notificationsApi } from '@/lib/api/notifications.api'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import {
  BellIcon,
  BellRingingIcon,
  CaretRightIcon,
  CheckIcon,
  CircleNotchIcon,
  EnvelopeIcon,
  MonitorIcon,
  PhoneIcon,
  SpeakerHighIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react'
import { toast } from 'sonner'

import type { FeatureFlagKeyType } from '@scrambl/shared'
import type { NotificationAudience } from '@scrambl/shared/notifications/notification-preference-catalog'
import {
  isNotificationChannelDefaultEnabled,
  NotificationChannel,
  NotificationPreference,
  NotificationType,
  UpdateNotificationPreferenceDto,
} from '@scrambl/shared'
import { flattenNotificationPreferenceCatalog } from '@scrambl/shared/notifications/notification-preference-catalog'

const logger = createLogger('components/notifications/NotificationPreferences')

interface NotificationPreferencesProps {
  className?: string
  notificationTypes?: readonly NotificationTypeConfig[]
  channelFilter?: readonly (NotificationChannel | 'BROWSER')[]
  getDefaultPreferenceValue?: (
    channel: NotificationChannel,
    type: NotificationType,
  ) => boolean
  loadPreferences?: () => Promise<NotificationPreference[]>
  updateMultiplePreferences?: (data: {
    preferences: UpdateNotificationPreferenceDto[]
  }) => Promise<unknown>
  resetPreferences?: () => Promise<unknown>
  showResetToDefaults?: boolean
  // Hide the permission banner + "Channels & devices" panel — for embeds
  // (e.g. the project-watcher section) that only manage the types grid
  showChannelSettings?: boolean
}

// Notification type configurations
export interface NotificationTypeConfig {
  type: NotificationType
  label: string
  description: string
  category: string
  channels?: readonly (NotificationChannel | 'BROWSER')[]
  /** Hide the row unless the org has this module enabled. */
  featureFlag?: FeatureFlagKeyType
  /** Account types allowed to see this row. Defaults to all of them. */
  audiences?: readonly NotificationAudience[]
}

/**
 * The full settings-page matrix, derived from the notification preference
 * catalog so every server-emitted type has a row. Embeds (client portal,
 * project watchers) pass their own narrower list via `notificationTypes`.
 */
export const NOTIFICATION_TYPES: readonly NotificationTypeConfig[] =
  flattenNotificationPreferenceCatalog()

/**
 * Below this many rows the matrix is small enough to read at a glance, so
 * groups render expanded (this is what the portal and project-watcher embeds
 * get). The full catalog is far larger and starts collapsed to its group
 * master toggles.
 */
const AUTO_EXPAND_TYPE_LIMIT = 12

const DEFAULT_DESKTOP_SETTINGS = {
  showBadgeCount: true,
  soundName: 'default',
  enableActions: true,
  persistNotifications: false,
  groupNotifications: true,
} as const

export const NotificationPreferences: FC<NotificationPreferencesProps> = ({
  className,
  notificationTypes = NOTIFICATION_TYPES,
  channelFilter,
  getDefaultPreferenceValue,
  loadPreferences: loadPreferencesProp,
  updateMultiplePreferences,
  resetPreferences,
  showResetToDefaults = true,
  showChannelSettings = true,
}) => {
  const { isPersonal } = useAccountType()
  const [preferences, setPreferences] = useState<NotificationPreference[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>(
    {},
  )
  const [isTesting, setIsTesting] = useState(false)
  const [smsStatus, setSmsStatus] = useState<SmsStatus | null>(null)
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('')
  const [smsCode, setSmsCode] = useState('')
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false)
  const [smsCodeSent, setSmsCodeSent] = useState(false)
  const [isSmsSaving, setIsSmsSaving] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  )

  // Module gating for preference rows. Embeds that pass their own type list
  // (client portal, project watchers) name no flags, so they never wait on it.
  const needsFeatureFlags = notificationTypes.some(config => config.featureFlag)
  const { checkFeatureFlag, isLoading: areFeatureFlagsLoading } =
    useFeatureFlags()

  // Browser notification integration
  const {
    preferences: browserPrefs,
    isSupported: browserSupported,
    permissionStatus,
    enableBrowserNotifications,
    disableBrowserNotifications,
    updateNotificationTypes,
    updateDisplaySettings,
    updateDesktopSettings,
    requestPermission,
    isDesktopApp,
    // This hook also sets up the event listeners for browser notifications
  } = useBrowserNotifications()

  const desktopSettings = {
    ...DEFAULT_DESKTOP_SETTINGS,
    ...(browserPrefs.desktopSettings || {}),
  }

  const desktopLabel = isDesktopApp ? 'Desktop' : 'Browser'
  const desktopDescription = isDesktopApp
    ? 'Native notifications from the desktop app'
    : 'Desktop notifications from your browser'
  const desktopColor = isDesktopApp ? 'text-amber-600' : 'text-purple-600'

  // Slack DMs are only offered once the org workspace is connected with
  // notifications enabled AND this user linked their Slack account.
  const { data: slackWorkspace } = useSlackWorkspace({ enabled: !isPersonal })
  const { data: slackUserLink } = useSlackUserLink(undefined, {
    enabled:
      !isPersonal &&
      !!slackWorkspace &&
      slackWorkspace.status === 'ACTIVE' &&
      slackWorkspace.settings.notificationsEnabled !== false,
  })
  const slackAvailable =
    !isPersonal &&
    !!slackWorkspace &&
    slackWorkspace.status === 'ACTIVE' &&
    slackWorkspace.settings.notificationsEnabled !== false &&
    !!slackUserLink

  const allChannels = [
    {
      channel: NotificationChannel.IN_APP,
      label: 'In-App',
      description: 'Notifications within the application',
      icon: BellIcon,
      color: 'text-blue-600',
    },
    {
      channel: NotificationChannel.EMAIL,
      label: 'Email',
      description: 'Email notifications to your inbox',
      icon: EnvelopeIcon,
      color: 'text-green-600',
    },
    {
      channel: NotificationChannel.SMS,
      label: 'SMS',
      description: smsStatus?.enabled
        ? `Text messages to ${smsStatus.phoneNumber}`
        : 'Transactional text message alerts',
      icon: PhoneIcon,
      color: 'text-cyan-600',
    },
    {
      channel: 'BROWSER' as const,
      label: desktopLabel,
      description: desktopDescription,
      icon: MonitorIcon,
      color: desktopColor,
    },
    ...(slackAvailable
      ? [
          {
            channel: NotificationChannel.SLACK,
            label: 'Slack',
            description: 'Direct messages from the Scrambl Slack app',
            icon: SlackLogo,
            color: 'text-[#611f69]',
          },
        ]
      : []),
  ]
  const channels = channelFilter
    ? allChannels.filter(channelConfig =>
        channelFilter.includes(channelConfig.channel),
      )
    : allChannels
  const typeGridTemplateColumns = `minmax(0,1fr) repeat(${channels.length},56px)`

  // Map notification types to browser preference keys
  const getBrowserTypeKey = (
    notificationType: NotificationType,
  ): string | null => {
    const typeMap: Record<string, string> = {
      [NotificationType.TASK_ASSIGNED]: 'assignments',
      [NotificationType.TASK_STATUS_CHANGED]: 'tasks',
      [NotificationType.TASK_DUE_SOON]: 'deadlines',
      [NotificationType.TASK_OVERDUE]: 'deadlines',
      [NotificationType.TASK_COMMENT_ADDED]: 'comments',
      [NotificationType.TICKET_CREATED]: 'tasks',
      [NotificationType.TICKET_ASSIGNED]: 'assignments',
      [NotificationType.TICKET_UPDATED]: 'tasks',
      [NotificationType.TICKET_STATUS_CHANGED]: 'tasks',
      [NotificationType.TICKET_COMMENT_ADDED]: 'comments',
      [NotificationType.TICKET_CUSTOMER_REPLY]: 'comments',
      [NotificationType.COMMENT_MENTION]: 'mentions',
      [NotificationType.NOTE_MENTION]: 'mentions',
      [NotificationType.COMMENT_REPLY]: 'comments',
      [NotificationType.PROJECT_MEMBER_ADDED]: 'tasks',
      [NotificationType.SYSTEM_MAINTENANCE]: 'system',
    }
    const explicit = typeMap[notificationType]
    if (explicit) return explicit

    // Everything else falls into the same six coarse buckets. Desktop/browser
    // popups are stored as category flags (BrowserNotificationPreferences
    // .enabledTypes), not per type, so several rows deliberately share a
    // bucket — the alternative is a dead column for most of the catalog.
    const type = String(notificationType)
    if (type.endsWith('_MENTION')) return 'mentions'
    if (
      type.endsWith('_COMMENT_ADDED') ||
      type.endsWith('_REPLY') ||
      type === NotificationType.THREAD_MESSAGE ||
      type === NotificationType.PROJECT_WATCH_COMMENTS
    ) {
      return 'comments'
    }
    if (type.endsWith('_ASSIGNED')) return 'assignments'
    if (
      type.endsWith('_REMINDER') ||
      type.endsWith('_DEADLINE') ||
      type.endsWith('_DUE') ||
      type.endsWith('_DUE_SOON') ||
      type.endsWith('_OVERDUE') ||
      type.endsWith('_AT_RISK') ||
      type.endsWith('_EXPIRING') ||
      type.startsWith('BUDGET_USAGE') ||
      type === NotificationType.BUDGET_OVERAGE ||
      type === NotificationType.BUDGET_PROJECTED_OVERAGE ||
      type === NotificationType.AUTHORIZATION_LOW_REMAINING ||
      type === NotificationType.TASK_SLA_BREACHED ||
      type === NotificationType.CONTRACT_TERM_ENDING ||
      type === NotificationType.CONTRACT_MONTH_TO_MONTH_ACTIVE ||
      type === NotificationType.DEMAND_APPROACHING_START
    ) {
      return 'deadlines'
    }
    if (
      type.startsWith('TASK_') ||
      type.startsWith('TICKET_') ||
      type.startsWith('PROJECT_') ||
      type.startsWith('SPRINT_')
    ) {
      return 'tasks'
    }
    return 'system'
  }

  const loadPreferences = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await (loadPreferencesProp
        ? loadPreferencesProp()
        : notificationsApi.getPreferences())
      setPreferences(data)
    } catch (error) {
      logger.error('Error loading notification preferences:', error)
      toast.error('Failed to load notification preferences')
    } finally {
      setIsLoading(false)
    }
  }, [loadPreferencesProp])

  // Load preferences on mount
  useEffect(() => {
    loadPreferences()
  }, [loadPreferences])

  const loadSmsStatus = useCallback(async () => {
    if (!showChannelSettings) return
    try {
      const status = await notificationsApi.getSmsStatus()
      setSmsStatus(status)
      setSmsPhoneNumber(status.phoneNumber ?? '')
    } catch (error) {
      logger.error('Error loading SMS status:', error)
    }
  }, [showChannelSettings])

  useEffect(() => {
    void loadSmsStatus()
  }, [loadSmsStatus])

  const requestSmsCode = async () => {
    try {
      setIsSmsSaving(true)
      const result = await notificationsApi.requestSmsVerification({
        phoneNumber: smsPhoneNumber,
        consentAccepted: smsConsentAccepted,
      })
      setSmsPhoneNumber(result.phoneNumber)
      setSmsCodeSent(true)
      toast.success('Verification code sent')
    } catch (error) {
      logger.error('Failed to request SMS verification:', error)
      toast.error(
        error instanceof Error ? error.message : 'Could not send the code',
      )
    } finally {
      setIsSmsSaving(false)
    }
  }

  const confirmSmsCode = async () => {
    try {
      setIsSmsSaving(true)
      const status = await notificationsApi.confirmSmsVerification({
        phoneNumber: smsPhoneNumber,
        code: smsCode,
      })
      setSmsStatus(status)
      setSmsCode('')
      setSmsCodeSent(false)
      setSmsConsentAccepted(false)
      toast.success('SMS notifications are ready')
    } catch (error) {
      logger.error('Failed to verify SMS code:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'The code could not be verified',
      )
    } finally {
      setIsSmsSaving(false)
    }
  }

  const revokeSms = async () => {
    try {
      setIsSmsSaving(true)
      const status = await notificationsApi.revokeSmsConsent()
      setSmsStatus(status)
      setPendingChanges(current =>
        Object.fromEntries(
          Object.entries(current).filter(([key]) => !key.startsWith('SMS-')),
        ),
      )
      toast.success('SMS notifications turned off')
    } catch (error) {
      logger.error('Failed to disable SMS:', error)
      toast.error('Could not turn off SMS notifications')
    } finally {
      setIsSmsSaving(false)
    }
  }

  // Get preference value for a channel/type combination
  const getPreferenceValue = (
    channel: NotificationChannel | 'BROWSER',
    type: NotificationType,
  ): boolean => {
    const key = `${channel}-${type}`

    // Check pending changes first
    if (key in pendingChanges) {
      return pendingChanges[key]
    }

    // Handle browser notifications
    if (channel === 'BROWSER') {
      if (!browserSupported || !browserPrefs.enabled) return false

      const browserTypeKey = getBrowserTypeKey(type)
      if (!browserTypeKey) return false

      return (
        browserPrefs.enabledTypes?.[
          browserTypeKey as keyof typeof browserPrefs.enabledTypes
        ] ?? true
      )
    }

    // Find existing preference for regular channels
    const existing = preferences.find(
      p => p.channel === channel && p.type === type,
    )

    if (existing) {
      return existing.enabled
    }

    if (getDefaultPreferenceValue) {
      return getDefaultPreferenceValue(channel, type)
    }

    // Shared with the API's preference resolution so an untouched toggle shows
    // what delivery will actually do. These used to be two hand-maintained
    // lists and had drifted apart.
    return isNotificationChannelDefaultEnabled(channel, type)
  }

  // Handle preference change
  const handlePreferenceChange = (
    channel: NotificationChannel | 'BROWSER',
    type: NotificationType,
    enabled: boolean,
  ) => {
    // Handle browser notifications separately
    if (channel === 'BROWSER') {
      const browserTypeKey = getBrowserTypeKey(type)
      if (browserTypeKey) {
        updateNotificationTypes({ [browserTypeKey]: enabled })
      }
      return
    }

    const key = `${channel}-${type}`
    setPendingChanges(prev => ({
      ...prev,
      [key]: enabled,
    }))
    setHasChanges(true)
  }

  // Save all pending changes
  const saveChanges = async () => {
    if (!hasChanges) return

    try {
      setIsSaving(true)

      const updates: UpdateNotificationPreferenceDto[] = Object.entries(
        pendingChanges,
      ).map(([key, enabled]) => {
        const [channel, type] = key.split('-')
        return {
          channel: channel as NotificationChannel,
          type: type as NotificationType,
          enabled,
        }
      })

      if (updateMultiplePreferences) {
        await updateMultiplePreferences({ preferences: updates })
      } else {
        await notificationsApi.updateMultiplePreferences({
          preferences: updates,
        })
      }

      // Reload preferences to get fresh data
      await loadPreferences()

      // Clear pending changes
      setPendingChanges({})
      setHasChanges(false)

      toast.success('Notification preferences updated successfully')
    } catch (error) {
      logger.error('Error saving notification preferences:', error)
      toast.error('Failed to save notification preferences')
    } finally {
      setIsSaving(false)
    }
  }

  // Reset to defaults
  const resetToDefaults = async () => {
    try {
      setIsSaving(true)
      if (resetPreferences) {
        await resetPreferences()
      } else {
        await notificationsApi.setDefaultPreferences()
      }
      await loadPreferences()
      setPendingChanges({})
      setHasChanges(false)
      toast.success('Notification preferences reset to defaults')
    } catch (error) {
      logger.error('Error resetting preferences:', error)
      toast.error('Failed to reset preferences')
    } finally {
      setIsSaving(false)
    }
  }

  // Test notification
  const sendTestNotification = async () => {
    setIsTesting(true)
    try {
      if (isTauri()) {
        // Use Tauri native notification
        const { isPermissionGranted, requestPermission, sendNotification } =
          await import('@tauri-apps/plugin-notification')
        const { invoke } = await import('@tauri-apps/api/core')

        let permissionGranted = await isPermissionGranted()
        logger.info('Tauri notification permission granted:', permissionGranted)

        if (!permissionGranted) {
          const permission = await requestPermission()
          permissionGranted = permission === 'granted'
          logger.info(
            'Tauri notification permission after request:',
            permission,
          )
        }

        if (!permissionGranted) {
          toast.error(
            'Notification permission denied. Please enable notifications in System Settings > Notifications > Scrambl',
          )
          return
        }

        await sendNotification({
          title: 'Test Notification',
          body: 'This is a test notification from Scrambl. If you see this, notifications are working!',
        })

        // Also test the badge count
        try {
          await invoke('set_badge_count', { count: 1 })
          logger.info('Badge count set to 1 for testing')
        } catch (badgeError) {
          logger.warn('Failed to set badge count:', badgeError)
        }

        toast.success(
          'Test notification sent! Check your notification center and dock icon badge.',
        )
      } else {
        // Use browser notification
        if (Notification.permission === 'granted') {
          new Notification('Test Notification', {
            body: 'This is a test notification from Scrambl. If you see this, notifications are working!',
            icon: '/favicon.ico',
          })
          toast.success('Test notification sent!')
        } else if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission()
          if (permission === 'granted') {
            new Notification('Test Notification', {
              body: 'This is a test notification from Scrambl. If you see this, notifications are working!',
              icon: '/favicon.ico',
            })
            toast.success('Test notification sent!')
          } else {
            toast.error('Notification permission denied')
          }
        } else {
          toast.error(
            'Notification permission denied. Please enable in browser settings.',
          )
        }
      }
    } catch (error) {
      logger.error('Failed to send test notification:', error)
      toast.error(
        `Failed to send test notification: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    } finally {
      setIsTesting(false)
    }
  }

  // Two independent gates, per the personal/work contract: business chrome is
  // hidden from personal spaces by account type, and every module-owned row is
  // additionally hidden when the org does not have that module enabled.
  const audience: NotificationAudience = isPersonal ? 'PERSONAL' : 'BUSINESS'
  const visibleNotificationTypes = notificationTypes.filter(config => {
    if (config.audiences && !config.audiences.includes(audience)) return false
    return !config.featureFlag || checkFeatureFlag(config.featureFlag)
  })

  // Group notification types by category
  const groupedTypes = visibleNotificationTypes.reduce(
    (acc, config) => {
      if (!acc[config.category]) {
        acc[config.category] = []
      }
      acc[config.category].push(config)
      return acc
    },
    {} as Record<string, NotificationTypeConfig[]>,
  )

  const defaultGroupsExpanded =
    visibleNotificationTypes.length <= AUTO_EXPAND_TYPE_LIMIT
  const isGroupExpanded = (category: string) =>
    expandedGroups[category] ?? defaultGroupsExpanded
  const toggleGroup = (category: string) =>
    setExpandedGroups(current => ({
      ...current,
      [category]: !(current[category] ?? defaultGroupsExpanded),
    }))

  // A group master toggle only speaks for the rows that actually offer this
  // channel (the weekly digest, for example, is email-only).
  const groupTypesForChannel = (
    types: NotificationTypeConfig[],
    channel: NotificationChannel | 'BROWSER',
  ) =>
    types.filter(
      config => !config.channels || config.channels.includes(channel),
    )

  const setGroupPreference = (
    types: NotificationTypeConfig[],
    channel: NotificationChannel | 'BROWSER',
    enabled: boolean,
  ) => {
    const applicable = groupTypesForChannel(types, channel)
    if (applicable.length === 0) return

    // Browser prefs live in a separate per-category blob — write them in one
    // pass so the group toggle does not race itself across N saves.
    if (channel === 'BROWSER') {
      const updates = applicable.reduce<Record<string, boolean>>(
        (acc, config) => {
          const browserTypeKey = getBrowserTypeKey(config.type)
          if (browserTypeKey) acc[browserTypeKey] = enabled
          return acc
        },
        {},
      )
      if (Object.keys(updates).length > 0) updateNotificationTypes(updates)
      return
    }

    setPendingChanges(prev => ({
      ...prev,
      ...Object.fromEntries(
        applicable.map(config => [`${channel}-${config.type}`, enabled]),
      ),
    }))
    setHasChanges(true)
  }

  // The full catalog is feature-flag gated, so rendering before the flags land
  // would flash rows for modules the org does not have.
  if (isLoading || (needsFeatureFlags && areFeatureFlagsLoading)) {
    return (
      <div className={cn('mx-auto max-w-4xl space-y-3', className)}>
        {[1, 2].map(i => (
          <section
            key={i}
            className="overflow-hidden rounded-xl border border-border/60 bg-card"
          >
            <div className="h-9 animate-pulse border-b border-border/60 bg-muted/40" />
            <div className="space-y-3 p-4">
              <div className="h-4 w-2/5 animate-pulse rounded bg-muted" />
              <div className="h-12 animate-pulse rounded bg-muted/50" />
              <div className="h-12 animate-pulse rounded bg-muted/50" />
            </div>
          </section>
        ))}
      </div>
    )
  }

  // Browser/desktop notification setup state
  const needsPermissionSetup =
    browserSupported &&
    (permissionStatus !== 'granted' ||
      (permissionStatus === 'granted' && !browserPrefs.enabled))
  const browserChannelDisabled =
    !browserSupported || !browserPrefs.enabled || permissionStatus !== 'granted'

  return (
    <div className={cn('mx-auto max-w-4xl space-y-3 pb-20', className)}>
      {/* ----------------------------------------------------------------- */}
      {/* Permission banner (conditional)                                   */}
      {/* ----------------------------------------------------------------- */}
      {showChannelSettings && needsPermissionSetup && (
        <section className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20">
          <div className="flex items-start gap-3 p-4">
            <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              <WarningCircleIcon className="h-4 w-4" weight="fill" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {permissionStatus === 'granted'
                  ? `${desktopLabel} notifications are disabled`
                  : `Enable ${desktopLabel.toLowerCase()} notifications`}
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                {permissionStatus === 'denied'
                  ? isDesktopApp
                    ? 'Notifications are blocked at the system level. Enable them in System Settings → Notifications → Scrambl.'
                    : 'Notifications are blocked at the browser level. Enable them in your browser settings.'
                  : permissionStatus === 'granted'
                    ? 'You have permission but notifications are turned off.'
                    : `Get notified even when Scrambl isn't focused.`}
              </p>
            </div>
            {(permissionStatus === 'default' ||
              (permissionStatus === 'granted' && !browserPrefs.enabled)) && (
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    if (permissionStatus === 'granted') {
                      await enableBrowserNotifications()
                    } else {
                      await requestPermission()
                      if (browserPrefs.permissionStatus === 'granted') {
                        await enableBrowserNotifications()
                      }
                    }
                  } catch (error) {
                    logger.error(
                      'Failed to enable browser notifications:',
                      error,
                    )
                  }
                }}
                className="flex-shrink-0"
              >
                Enable
              </Button>
            )}
          </div>
        </section>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Channels & devices                                                */}
      {/* ----------------------------------------------------------------- */}
      {showChannelSettings && (
        <section className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <header className="flex items-center justify-between gap-2 border-b border-border/60 bg-muted/40 px-4 py-2">
            <div className="flex items-center gap-1.5">
              <BellIcon
                className="h-3.5 w-3.5 text-muted-foreground"
                weight="fill"
              />
              <h3 className="text-sm font-semibold">Channels &amp; devices</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={sendTestNotification}
              disabled={isTesting}
              className="h-7 gap-1 px-2 text-xs"
            >
              {isTesting ? (
                <>
                  <CircleNotchIcon className="h-3.5 w-3.5 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <BellRingingIcon className="h-3.5 w-3.5" />
                  Send test
                </>
              )}
            </Button>
          </header>

          <ul className="divide-y divide-border/60">
            {/* Desktop / browser notifications */}
            <li className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <MonitorIcon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {desktopLabel} notifications
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {desktopDescription}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={browserPrefs.enabled}
                  onCheckedChange={enabled => {
                    if (enabled) void enableBrowserNotifications()
                    else void disableBrowserNotifications()
                  }}
                  disabled={!browserSupported || permissionStatus !== 'granted'}
                />
              </div>

              {isDesktopApp && (
                <div
                  className={cn(
                    'mt-3 grid gap-1 rounded-md border border-border/60 bg-muted/15 p-1.5 sm:grid-cols-2',
                    !browserPrefs.enabled && 'pointer-events-none opacity-50',
                  )}
                >
                  {[
                    {
                      id: 'desktop-badge',
                      label: 'Show badge count',
                      value: desktopSettings.showBadgeCount ?? true,
                      onChange: (v: boolean) =>
                        void updateDesktopSettings({ showBadgeCount: v }),
                    },
                    {
                      id: 'desktop-group',
                      label: 'Group notifications',
                      value: desktopSettings.groupNotifications ?? true,
                      onChange: (v: boolean) =>
                        void updateDesktopSettings({ groupNotifications: v }),
                    },
                    {
                      id: 'desktop-actions',
                      label: 'Show action buttons',
                      value: desktopSettings.enableActions ?? true,
                      onChange: (v: boolean) =>
                        void updateDesktopSettings({ enableActions: v }),
                    },
                    {
                      id: 'desktop-persist',
                      label: 'Keep visible until dismissed',
                      value: desktopSettings.persistNotifications ?? false,
                      onChange: (v: boolean) =>
                        void updateDesktopSettings({ persistNotifications: v }),
                    },
                  ].map(item => (
                    <label
                      key={item.id}
                      htmlFor={item.id}
                      className="flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-background"
                    >
                      <span className="text-xs">{item.label}</span>
                      <Switch
                        id={item.id}
                        checked={item.value}
                        onCheckedChange={item.onChange}
                        disabled={!browserPrefs.enabled}
                      />
                    </label>
                  ))}
                </div>
              )}
            </li>

            {/* Transactional SMS opt-in and phone verification */}
            <li className="px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300">
                  <PhoneIcon className="h-3.5 w-3.5" weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">SMS notifications</p>
                      <p className="text-xs text-muted-foreground">
                        Transactional alerts and workflow reminders from Scrambl
                      </p>
                    </div>
                    {smsStatus?.enabled && (
                      <Badge
                        variant="outline"
                        className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300"
                      >
                        <CheckIcon className="mr-1 h-3 w-3" weight="bold" />
                        Verified
                      </Badge>
                    )}
                  </div>

                  {smsStatus?.enabled ? (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/15 px-3 py-2.5">
                      <div>
                        <p className="text-sm font-medium">
                          {smsStatus.phoneNumber}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          SMS types can be selected in the matrix below.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void revokeSms()}
                        disabled={isSmsSaving}
                      >
                        Turn off SMS
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-3 space-y-3 rounded-md border border-border/60 bg-muted/15 p-3">
                      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                        <Input
                          type="tel"
                          value={smsPhoneNumber}
                          onChange={event =>
                            setSmsPhoneNumber(event.target.value)
                          }
                          placeholder="+1 (555) 555-0123"
                          aria-label="Mobile phone number"
                          disabled={smsCodeSent || isSmsSaving}
                        />
                        {smsCodeSent ? (
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSmsCodeSent(false)
                              setSmsCode('')
                            }}
                            disabled={isSmsSaving}
                          >
                            Change number
                          </Button>
                        ) : (
                          <Button
                            onClick={() => void requestSmsCode()}
                            disabled={
                              isSmsSaving ||
                              !smsPhoneNumber.trim() ||
                              !smsConsentAccepted
                            }
                          >
                            {isSmsSaving ? 'Sending…' : 'Send code'}
                          </Button>
                        )}
                      </div>

                      {smsCodeSent && (
                        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                          <Input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            value={smsCode}
                            onChange={event =>
                              setSmsCode(
                                event.target.value
                                  .replace(/\D/g, '')
                                  .slice(0, 6),
                              )
                            }
                            placeholder="6-digit verification code"
                            aria-label="SMS verification code"
                          />
                          <Button
                            onClick={() => void confirmSmsCode()}
                            disabled={isSmsSaving || smsCode.length !== 6}
                          >
                            {isSmsSaving ? 'Verifying…' : 'Verify & enable'}
                          </Button>
                        </div>
                      )}

                      {!smsCodeSent && (
                        <label className="flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed text-muted-foreground">
                          <Checkbox
                            checked={smsConsentAccepted}
                            onCheckedChange={checked =>
                              setSmsConsentAccepted(checked === true)
                            }
                            aria-label="Agree to receive transactional SMS messages"
                            className="mt-0.5"
                          />
                          <span>
                            By checking this box and clicking Send code, you
                            agree to receive recurring transactional text
                            messages from Scrambl about account activity, tasks,
                            appointments, invoices, and workflow alerts. Message
                            frequency varies. Message and data rates may apply.
                            Reply STOP to opt out or HELP for help. Consent is
                            not a condition of purchase.{' '}
                            <a
                              href="https://scrambl.com/terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline underline-offset-2 hover:text-foreground"
                            >
                              Terms
                            </a>{' '}
                            and{' '}
                            <a
                              href="https://scrambl.com/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline underline-offset-2 hover:text-foreground"
                            >
                              Privacy Policy
                            </a>
                            .
                          </span>
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>

            {/* Notification sounds */}
            <li className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <SpeakerHighIcon className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">Notification sounds</p>
                  <p className="text-xs text-muted-foreground">
                    Play a sound when new messages arrive in threads
                  </p>
                </div>
              </div>
              <Switch
                checked={browserPrefs.playSound ?? true}
                onCheckedChange={enabled =>
                  updateDisplaySettings({ playSound: enabled })
                }
              />
            </li>
          </ul>
        </section>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Notification types matrix                                         */}
      {/* ----------------------------------------------------------------- */}
      <section className="overflow-hidden rounded-xl border border-border/60 bg-card">
        <header className="flex items-center gap-1.5 border-b border-border/60 bg-muted/40 px-4 py-2">
          <h3 className="text-sm font-semibold">Notification types</h3>
        </header>

        {/* Channel column header */}
        <div
          className="grid items-center gap-2 border-b border-border/60 bg-muted/15 px-4 py-2"
          style={{ gridTemplateColumns: typeGridTemplateColumns }}
        >
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Type
          </div>
          {channels.map(channelConfig => {
            const IconComponent = channelConfig.icon
            const isBrowserChannel = channelConfig.channel === 'BROWSER'
            const isDimmed = isBrowserChannel && browserChannelDisabled
            return (
              <div
                key={channelConfig.channel}
                className={cn(
                  'flex flex-col items-center gap-0.5',
                  isDimmed && 'opacity-50',
                )}
                title={
                  isBrowserChannel && browserChannelDisabled
                    ? `${desktopLabel} notifications are disabled`
                    : channelConfig.description
                }
              >
                <IconComponent
                  className={cn('h-3.5 w-3.5', channelConfig.color)}
                />
                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {channelConfig.label}
                </span>
              </div>
            )
          })}
        </div>

        {/* Grouped type rows */}
        <div className="divide-y divide-border/60">
          {Object.entries(groupedTypes).map(([category, types]) => {
            const isExpanded = isGroupExpanded(category)
            return (
              <div key={category}>
                <div
                  className="grid items-center gap-2 border-b border-border/60 bg-muted/15 px-4 py-1.5"
                  style={{ gridTemplateColumns: typeGridTemplateColumns }}
                >
                  <button
                    type="button"
                    onClick={() => toggleGroup(category)}
                    aria-expanded={isExpanded}
                    className="flex min-w-0 items-center gap-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <CaretRightIcon
                      className={cn(
                        'h-3 w-3 flex-none transition-transform',
                        isExpanded && 'rotate-90',
                      )}
                      weight="bold"
                      aria-hidden="true"
                    />
                    <span className="truncate">{category}</span>
                    <span className="flex-none font-normal normal-case tracking-normal">
                      ({types.length})
                    </span>
                  </button>
                  {channels.map(channelConfig => {
                    const applicable = groupTypesForChannel(
                      types,
                      channelConfig.channel,
                    )
                    const isBrowserChannel = channelConfig.channel === 'BROWSER'
                    const isDisabled =
                      applicable.length === 0 ||
                      (isBrowserChannel && browserChannelDisabled) ||
                      (channelConfig.channel === NotificationChannel.SMS &&
                        !smsStatus?.enabled)
                    const enabledCount = applicable.filter(config =>
                      getPreferenceValue(channelConfig.channel, config.type),
                    ).length
                    const allEnabled =
                      applicable.length > 0 &&
                      enabledCount === applicable.length
                    const isMixed = enabledCount > 0 && !allEnabled
                    const anyEnabled = enabledCount > 0

                    return (
                      <div
                        key={`${category}-${channelConfig.channel}-all`}
                        className="flex flex-col items-center justify-center"
                      >
                        <Switch
                          aria-label={`All ${channelConfig.label} notifications for ${category}`}
                          aria-description={
                            isMixed
                              ? `${enabledCount} of ${applicable.length} on. Turn off to disable all notifications in this group.`
                              : undefined
                          }
                          title={
                            isMixed
                              ? `${enabledCount} of ${applicable.length} on`
                              : undefined
                          }
                          checked={anyEnabled}
                          onCheckedChange={() =>
                            setGroupPreference(
                              types,
                              channelConfig.channel,
                              !anyEnabled,
                            )
                          }
                          disabled={isDisabled}
                          className={cn('scale-90', isDisabled && 'opacity-50')}
                        />
                        {isMixed && (
                          <span className="text-[10px] leading-tight text-muted-foreground">
                            {enabledCount}/{applicable.length} on
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
                {isExpanded && (
                  <ul className="divide-y divide-border/60">
                    {types.map(typeConfig => (
                      <li
                        key={typeConfig.type}
                        className="grid items-center gap-2 px-4 py-2.5 transition-colors hover:bg-muted/20"
                        style={{ gridTemplateColumns: typeGridTemplateColumns }}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium">
                            {typeConfig.label}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {typeConfig.description}
                          </p>
                        </div>
                        {channels.map(channelConfig => {
                          const isAvailable =
                            !typeConfig.channels ||
                            typeConfig.channels.includes(channelConfig.channel)
                          const isBrowserChannel =
                            channelConfig.channel === 'BROWSER'
                          const isDisabled =
                            (isBrowserChannel && browserChannelDisabled) ||
                            (channelConfig.channel ===
                              NotificationChannel.SMS &&
                              !smsStatus?.enabled)

                          if (!isAvailable) {
                            return (
                              <div
                                key={`${typeConfig.type}-${channelConfig.channel}`}
                                className="flex justify-center text-muted-foreground"
                                title={`${channelConfig.label} notifications are not available for ${typeConfig.label.toLowerCase()}`}
                              >
                                <span aria-hidden="true">—</span>
                                <span className="sr-only">
                                  {channelConfig.label} notifications are not
                                  available for {typeConfig.label.toLowerCase()}
                                </span>
                              </div>
                            )
                          }

                          return (
                            <div
                              key={`${typeConfig.type}-${channelConfig.channel}`}
                              className="flex justify-center"
                            >
                              <Switch
                                aria-label={`${channelConfig.label} notifications for ${typeConfig.label}`}
                                checked={getPreferenceValue(
                                  channelConfig.channel,
                                  typeConfig.type,
                                )}
                                onCheckedChange={enabled =>
                                  handlePreferenceChange(
                                    channelConfig.channel,
                                    typeConfig.type,
                                    enabled,
                                  )
                                }
                                disabled={isDisabled}
                                className={isDisabled ? 'opacity-50' : ''}
                              />
                            </div>
                          )
                        })}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* Sticky save bar                                                   */}
      {/* ----------------------------------------------------------------- */}
      {hasChanges && (
        <div className="sticky bottom-4 z-10">
          <div className="mx-auto flex items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-primary/10">
            <div className="flex items-center gap-2 text-sm">
              <Badge
                variant="outline"
                className="h-5 rounded-md border-primary/30 bg-primary/10 px-1.5 text-[11px] font-normal text-primary"
              >
                Unsaved
              </Badge>
              <span className="text-muted-foreground">
                You have unsaved notification changes
              </span>
            </div>
            <div className="flex flex-shrink-0 gap-2">
              {showResetToDefaults && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetToDefaults}
                  disabled={isSaving}
                >
                  Reset to defaults
                </Button>
              )}
              <Button
                size="sm"
                onClick={saveChanges}
                disabled={isSaving}
                className="min-w-[100px]"
              >
                {isSaving ? (
                  <>
                    <CircleNotchIcon className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <CheckIcon className="mr-2 h-3.5 w-3.5" />
                    Save changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
